import * as reactIntl from 'react-intl';
import * as yup from 'yup';

import { TrlFormattedMessagePlain } from '»utils/i18n';

yup.addMethod(yup.number, 'greaterThanZero', function greaterThanZero(message) {
  return this.test({
    name: 'greaterThanZero',
    message,
    test: (value) => value > 0,
  });
});

yup.addMethod(
  yup.number,
  'hasCorrectDecimalPlaces',
  function hasCorrectDecimalPlaces(
    mustBeIntegerMessage,
    maxDecimalPlacesMessage,
    isFunds,
    quantityPrecision,
  ) {
    return this.test({
      name: 'hasCorrectDecimalPlaces',
      test: (value, context) => {
        const { createError } = context;
        const { selectOrderType } = context.parent;

        if (isFunds && selectOrderType === 3) {
          const decimalPart = value.toString().split('.')[1];

          if (decimalPart) {
            if (quantityPrecision === 0)
              return createError({ message: mustBeIntegerMessage });

            if (decimalPart.length > quantityPrecision)
              return createError({ message: maxDecimalPlacesMessage });
          }

          return true;
        }

        if (Number.isInteger(value)) return true;

        return createError({ message: mustBeIntegerMessage });
      },
    });
  },
);

yup.addMethod(
  yup.number,
  'fundsQuantityValidation',
  function fundsQuantityValidation(
    isFunds,
    quantityAvailable,
    initialAmount,
    additionalAmount,
    lastPx,
    qtyErrorMessage,
    amountErrorMessage,
  ) {
    return this.test({
      name: 'fundsQuantityValidation',
      test: (value, context) => {
        const { createError } = context;
        const { selectOrderType } = context.parent;

        if (isFunds) {
          if (!quantityAvailable) {
            if (selectOrderType === 3) {
              const minQty =
                !initialAmount || !lastPx ? 0 : initialAmount / lastPx;
              if (value < minQty)
                return createError({ message: qtyErrorMessage(minQty) });

              return true;
            }

            if (value < initialAmount)
              return createError({
                message: amountErrorMessage(initialAmount),
              });

            return true;
          }

          if (selectOrderType === 3) {
            const minQty =
              !additionalAmount || !lastPx ? 0 : additionalAmount / lastPx;
            if (value < minQty)
              return createError({ message: qtyErrorMessage(minQty) });

            return true;
          }

          if (value < additionalAmount)
            return createError({
              message: amountErrorMessage(additionalAmount),
            });
        }

        return true;
      },
    });
  },
);

export const trlInsertOrderValidation = ({
  quantityAvailable,
  activeStep,
  useOtp,
  instrumentCurrency,
  isFunds,
  quantityPrecision,
  initialAmount,
  additionalAmount,
  lastPx,
}) => {
  const intl = reactIntl.useIntl();

  const requiredFieldMessage = intl.formatMessage({
    id: 'TRLT_UI-REQUIRED_FIELD',
  });

  const maxDecimalPlacesMessage = intl.formatMessage(
    {
      id: 'TRLT_UI-DECIMAL_PLACES_LOWER_THAN',
    },
    { min: quantityPrecision },
  );

  const qtyGreaterThanZeroMessage = intl.formatMessage(
    {
      id: 'TRLT_UI-QTY_BIGGER_THAN',
    },
    { min: 0 },
  );

  const priceGreaterThanZeroMessage = intl.formatMessage(
    {
      id: 'TRLT_UI-PX_BIGGER_THAN',
    },
    { min: 0 },
  );

  const quantityMustBeIntegerMessage = intl.formatMessage({
    id: 'TRLT_UI-INTEGER_ONLY',
  });

  const fieldMustBeNumber = intl.formatMessage({
    id: 'TRLT_UI-NUMBER_FIELD',
  });

  const qtyGreaterOrEqual = (minValue) =>
    intl.formatMessage(
      {
        id: 'TRLT_UI-QUANTITY_BIGGER',
      },
      { min: minValue },
    );

  const amountGreaterOrEqual = (minValue) =>
    intl.formatMessage(
      {
        id: 'TRLT_UI-AMOUNT_BIGGER',
      },
      { min: minValue },
    );

  return yup.object().shape({
    selectSecurityAccount: yup.string().required(),
    selectBalanceAccount: yup.string().required(
      intl.formatMessage(
        {
          id: 'TRLT_UI-CURRENCY_NON_EXISTENT',
        },
        { currency: instrumentCurrency },
      ),
    ),
    selectOrderType: yup.number('NUMBER').required(),
    selectOperation: yup.mixed().required(),
    inputQuantity: yup
      .number('NUMBER')
      .typeError(fieldMustBeNumber)
      .when(['selectOperation'], {
        is: (selectOperation) =>
          selectOperation === 2 || selectOperation === 'E',
        then: () =>
          yup
            .number('NUMBER')
            .typeError(fieldMustBeNumber)
            .hasCorrectDecimalPlaces(
              quantityMustBeIntegerMessage,
              maxDecimalPlacesMessage,
              isFunds,
              quantityPrecision,
            )
            .test({
              name: 'isQuantityValid',
              test: function isQuantityValid(value) {
                if (quantityAvailable === 0)
                  return this.createError({
                    message: intl.formatMessage({
                      id: isFunds ? 'TRLT_UI-CANT_RESCUE' : 'TRLT_UI-CANT_SELL',
                    }),
                  });

                if (value > quantityAvailable)
                  return this.createError({
                    message: intl.formatMessage(
                      { id: 'TRLT_UI-QUANTITY_SMALLER' },
                      { max: quantityAvailable },
                    ),
                  });

                return true;
              },
            })
            .greaterThanZero(qtyGreaterThanZeroMessage)
            .required(requiredFieldMessage)
            .fundsQuantityValidation(
              isFunds,
              quantityAvailable,
              initialAmount,
              additionalAmount,
              lastPx,
              qtyGreaterOrEqual,
              amountGreaterOrEqual,
            ),
        otherwise: () =>
          yup
            .number('NUMBER')
            .typeError(fieldMustBeNumber)
            .hasCorrectDecimalPlaces(
              quantityMustBeIntegerMessage,
              maxDecimalPlacesMessage,
              isFunds,
              quantityPrecision,
            )
            .greaterThanZero(qtyGreaterThanZeroMessage)
            .required(requiredFieldMessage)
            .fundsQuantityValidation(
              isFunds,
              quantityAvailable,
              initialAmount,
              additionalAmount,
              lastPx,
              qtyGreaterOrEqual,
              amountGreaterOrEqual,
            ),
      }),
    selectPriceType: yup
      .mixed()
      .test('isRequired', requiredFieldMessage, function isRequired(value) {
        if (!isFunds && !value) return false;
        return true;
      }),
    inputPrice: yup
      .number('NUMBER')
      .typeError(fieldMustBeNumber)
      .when(['selectPriceType'], {
        is: (selectPriceType) => !isFunds && selectPriceType !== 1,
        then: () =>
          yup
            .number('NUMBER')
            .typeError(fieldMustBeNumber)
            .greaterThanZero(priceGreaterThanZeroMessage)
            .required(requiredFieldMessage),
        otherwise: () => yup.number('NUMBER').typeError(fieldMustBeNumber),
      }),
    selectExpirationDate: yup
      .string()
      .test('isValid', 'TRLT_UI-INVALID_DATE', function isValid(value) {
        if (value === 'Invalid Date') return false;
        return true;
      })
      .required(requiredFieldMessage),
    inputStopPrice: yup
      .number('NUMBER')
      .typeError(fieldMustBeNumber)
      .when(['selectOrderType'], {
        is: (selectOrderType) => selectOrderType !== 1 && !isFunds,
        then: () =>
          yup
            .number('NUMBER')
            .typeError(fieldMustBeNumber)
            .greaterThanZero(priceGreaterThanZeroMessage)
            .required(requiredFieldMessage),
      }),
    validationCode: yup
      .string()
      .test(
        'isRequired',
        TrlFormattedMessagePlain({ id: 'TRLT_UI-REQUIRED_FIELD' }),
        function isRequired(value) {
          if (useOtp && activeStep === 1 && !value) return false;

          return true;
        },
      ),
  });
};
